import { useLocation } from "react-router";
import type { MixPreferenceInput, MixView, Performance, Post, QueueItem, RepeatMode } from "../api/types";
import { REPEAT_LABEL } from "../lib/labels";
import { formatMs } from "../lib/time";
import { useServices } from "./services";

/**
 * 再生操作の送り口。どれも「受付」を返すだけで、結果は CommandTracker が確かめる。
 * いま見ている再生回（session_id・generation）とキューの版（revision）を一緒に送り、
 * サーバーが古い世代への後着の操作を拒めるようにする（仕様書「同時操作と状態同期」）
 */
export function usePlaybackActions() {
  const location = useLocation();
  const { commands, sync } = useServices();

  const base = () => {
    const { playback, queue } = sync.store.get();
    if (!playback) throw new Error("playback snapshot is missing");
    return {
      session_id: playback.session_id,
      generation: playback.generation,
      revision: queue?.revision ?? playback.revision,
    };
  };
  const commandBase = () => {
    const mix = sync.store.get().playback?.mix;
    return { ...base(), mix_id: mix?.mix_id ?? null, mix_revision: mix?.revision ?? null };
  };

  return {
    play: () => commands.sendCommand("再生", { kind: "play", ...commandBase() }),
    pause: () => commands.sendCommand("一時停止", { kind: "pause", ...commandBase() }),
    resume: () => commands.sendCommand("再開", { kind: "resume", ...commandBase() }),
    skip: () => commands.sendCommand("スキップ", { kind: "skip", ...base() }),
    stop: () => commands.sendCommand("再生の終了", { kind: "stop", ...commandBase() }),
    clear: () => commands.sendCommand("キューの全消去", { kind: "clear", ...base() }),
    seek: (ms: number) => commands.sendCommand(`${formatMs(ms)} へ移動`, { kind: "seek", position_ms: ms, ...base() }),
    repeat: (mode: RepeatMode) => commands.sendCommand(`リピート（${REPEAT_LABEL[mode]}）`, { kind: "repeat", repeat: mode, ...base() }),
    djMix: (enabled: boolean) => commands.sendCommand(`DJ Mixを${enabled ? "オン" : "オフ"}にする`, {
      kind: "dj_mix", dj_mix_enabled: enabled, ...commandBase(),
    }),
    mixPreference: (target: Pick<MixView, "mix_id" | "revision"> & { preference_mix_id: string; preference_revision: number }, preference: MixPreferenceInput | null) =>
      commands.sendCommand(preference ? "Mixの希望を保存" : "Mixの希望を解除", {
        ...base(), kind: preference ? "mix_preference" : "mix_preference_clear",
        mix_id: target.mix_id, mix_revision: target.revision,
        preference_mix_id: target.preference_mix_id, preference_revision: target.preference_revision,
        preference,
      }),
    enqueue: (p: Performance, post?: Post | null) =>
      commands.sendCommand(`「${p.work.title}」の追加`, {
        kind: "enqueue",
        segment_revision_id: p.segment.id,
        post_id: post?.id ?? null,
        ...base(),
      }),
    remove: (item: QueueItem) =>
      commands.sendCommand(`「${item.performance.work.title}」をキューから外す`, { kind: "remove", queue_item_id: item.id, ...base() }),
    reorder: (ids: string[], label: string) => commands.sendCommand(label, { kind: "reorder", item_ids: ids, ...base() }),
    capture: () => commands.capture("ここ好き", base(), location.key),
  };
}
